import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'image/webp'];

export default function ProfilePage() {
  const { user, isSuperuser, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [nome, setNome] = useState(user?.nome || '');
  const [email, setEmail] = useState(user?.email || '');
  // `avatar` guarda o caminho no bucket; `avatarPreview`, a URL assinada.
  const [avatar, setAvatar] = useState(user?.avatarPath || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');

  // Password fields
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);

  // Feedback states
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Active tab
  const [activeTab, setActiveTab] = useState<'info' | 'senha'>('info');

  // O perfil chega depois da primeira renderização: a sessão é restaurada e só
  // então o provider busca `profiles` e assina a URL da foto. Sem este sincronismo
  // os campos ficam com o que existia no primeiro render — em geral vazio — e a
  // foto salva não aparecia.
  useEffect(() => {
    if (!user) return;
    setNome(user.nome || '');
    setEmail(user.email || '');
    setAvatar(user.avatarPath || '');
    setAvatarPreview(user.avatar || '');
  }, [user]);

  const userIniciais = nome
    ? nome
        .split(' ')
        .map((n) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg('');

    if (file.size > 2 * 1024 * 1024) {
      setErrorMsg('A imagem deve ter no máximo 2MB.');
      return;
    }
    if (!TIPOS_ACEITOS.includes(file.type)) {
      setErrorMsg('Use uma imagem JPEG, PNG ou WebP.');
      return;
    }
    if (!user?.id) {
      setErrorMsg('Sessão expirada. Faça login novamente.');
      return;
    }

    // Sobe o arquivo na hora e guarda o **caminho**; a URL de exibição é
    // assinada, porque o bucket é privado. A versão anterior lia a imagem como
    // data URL e mantinha tudo em memória: trocava na tela e sumia no F5.
    const extensao = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const caminho = `${user.id}/perfil-${Date.now()}.${extensao}`;

    setSaving(true);
    const { error: erroUpload } = await supabase.storage
      .from('avatares')
      .upload(caminho, file, { contentType: file.type, upsert: true });

    if (erroUpload) {
      setSaving(false);
      setErrorMsg(`Erro ao enviar a imagem: ${erroUpload.message}`);
      return;
    }

    const { data: assinada } = await supabase.storage
      .from('avatares')
      .createSignedUrl(caminho, 60 * 60 * 12);

    setSaving(false);
    setAvatar(caminho);
    setAvatarPreview(assinada?.signedUrl || '');
  };

  const handleRemoveAvatar = () => {
    // Marca a remoção; o arquivo sai do bucket ao salvar, junto com a gravação
    // do perfil — remover aqui apagaria o arquivo mesmo se a pessoa desistisse
    // sem salvar.
    setAvatar('');
    setAvatarPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveInfo = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    if (!nome.trim()) {
      setErrorMsg('O nome é obrigatório.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      setErrorMsg('Insira um e-mail válido.');
      return;
    }

    setSaving(true);

    const result = await updateProfile({
      nome: nome.trim(),
      email: email.trim().toLowerCase(),
      // String vazia é remoção explícita; `null` diz isso ao backend.
      avatarPath: avatar || null,
    });

    if (result.error) {
      setErrorMsg(result.error);
      setSaving(false);
      return;
    }

    setSaving(false);
    setSuccessMsg('Perfil atualizado com sucesso!');
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordMsg('');

    if (!senhaAtual) {
      setPasswordError('Informe sua senha atual.');
      return;
    }
    if (!novaSenha || novaSenha.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setPasswordError('As senhas não coincidem.');
      return;
    }

    setSaving(true);

    const result = await changePassword(senhaAtual, novaSenha);

    if (result.error) {
      setPasswordError(result.error);
      setSaving(false);
      return;
    }

    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setSaving(false);
    setPasswordMsg('Senha alterada com sucesso!');
    setTimeout(() => setPasswordMsg(''), 3500);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <div className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="text-xl font-bold text-foreground-900">Meu Perfil</h1>
          </div>
          <p className="text-sm text-foreground-500 ml-11">
            Gerencie suas informações pessoais e credenciais de acesso.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Avatar section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative group cursor-pointer" onClick={handleAvatarClick}>
            {avatarPreview ? (
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-background-100">
                <img src={avatarPreview} alt={nome} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className={`w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold border-4 border-background-100 ${
                  isSuperuser ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                }`}
              >
                {userIniciais}
              </div>
            )}
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-camera-line text-background-50 text-xl"></i>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />
          <div className="flex gap-3 mt-3">
            <button
              onClick={handleAvatarClick}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-line mr-1"></i>
              Alterar foto
            </button>
            {avatarPreview && (
              <button
                onClick={handleRemoveAvatar}
                className="text-sm text-foreground-400 hover:text-red-500 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                Remover
              </button>
            )}
          </div>
          <div className="mt-4 text-center">
            <h2 className="text-lg font-semibold text-foreground-900">{nome || 'Usuário'}</h2>
            <div className="flex items-center justify-center gap-1.5 mt-0.5">
              <div
                className={`w-4 h-4 flex items-center justify-center ${isSuperuser ? 'text-accent-600' : 'text-primary-600'}`}
              >
                <i
                  className={`${isSuperuser ? 'ri-shield-star-fill' : 'ri-user-3-fill'} text-xs`}
                ></i>
              </div>
              <span className="text-sm text-foreground-500">
                {isSuperuser ? 'Super Admin' : 'Síndico / Gestor'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'info'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-user-line mr-1.5"></i>
              Informações
            </button>
            <button
              onClick={() => setActiveTab('senha')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'senha'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-lock-line mr-1.5"></i>
              Senha
            </button>
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'info' ? (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6">
            {errorMsg && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
                <i className="ri-error-warning-line"></i>
                <span>{errorMsg}</span>
              </div>
            )}
            {successMsg && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-5">
                <i className="ri-checkbox-circle-line"></i>
                <span>{successMsg}</span>
              </div>
            )}

            <div className="flex flex-col gap-5">
              <div>
                <label
                  htmlFor="profile-nome"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Nome completo
                </label>
                <input
                  id="profile-nome"
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="Seu nome completo"
                />
              </div>

              <div>
                <label
                  htmlFor="profile-email"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  E-mail
                </label>
                <input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!user?.emailVerificadoEm}
                  className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all disabled:cursor-not-allowed disabled:opacity-60"
                  placeholder="seu@email.com"
                />

                {/* Trocar exige ter confirmado o atual (P-24, ADR-0014). Quem
                    nunca provou posse do endereço que deu não troca por outro
                    que também não provou — a conta ficaria sem nenhum endereço
                    alcançável. Quem recusa de fato é o gatilho no banco;
                    desabilitar aqui explica antes da tentativa. */}
                {!user?.emailVerificadoEm && (
                  <p className="mt-1.5 text-sm text-foreground-600">
                    Confirme o e-mail atual para poder trocá-lo. Use o aviso no topo da página.
                  </p>
                )}
              </div>

              <div className="pt-2">
                <button
                  onClick={handleSaveInfo}
                  disabled={saving}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {saving ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6">
            {passwordError && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
                <i className="ri-error-warning-line"></i>
                <span>{passwordError}</span>
              </div>
            )}
            {passwordMsg && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-5">
                <i className="ri-checkbox-circle-line"></i>
                <span>{passwordMsg}</span>
              </div>
            )}

            <div className="flex flex-col gap-5">
              <div>
                <label
                  htmlFor="profile-senha-atual"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Senha atual
                </label>
                <div className="relative">
                  <input
                    id="profile-senha-atual"
                    type={showSenhaAtual ? 'text' : 'password'}
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className={`ri-${showSenhaAtual ? 'eye-off' : 'eye'}-line text-lg`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="profile-nova-senha"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Nova senha
                </label>
                <div className="relative">
                  <input
                    id="profile-nova-senha"
                    type={showNovaSenha ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                    placeholder="Mínimo 6 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNovaSenha(!showNovaSenha)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    <i className={`ri-${showNovaSenha ? 'eye-off' : 'eye'}-line text-lg`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="profile-confirmar-senha"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Confirmar nova senha
                </label>
                <input
                  id="profile-confirmar-senha"
                  type="password"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                  placeholder="Repita a nova senha"
                />
              </div>

              <div className="pt-2">
                <button
                  onClick={handleChangePassword}
                  disabled={saving}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {saving ? 'Alterando...' : 'Alterar senha'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
